const debug = (...args) => {
  if (process.env.NODE_ENV !== "production") {
    console.log(...args);
  }
};

export function reoptToDailySeries(results, dayIndex = 0, tph = 1, loadYear = null) {
  if (!results) return { data: [], isOffGrid: false };
  const outputs = results.outputs || {};

  // Helper to pick the first array among candidate keys, preferring any that have non-zero values.
  const pick = (obj, keys) => {
    if (!obj) return [];
    for (const k of keys) {
      const arr = obj[k];
      if (Array.isArray(arr) && arr.some((v) => Math.abs(v) > 1e-9)) return arr;
    }
    for (const k of keys) {
      const arr = obj[k];
      if (Array.isArray(arr)) return arr;
    }
    return [];
  };

  // Series lookups with multiple possible key names (covers REopt v2/v3 variants & potential future changes)
  const load = pick(outputs?.ElectricLoad, [
    "load_series_kw",
    "electric_load_series_kw",
  ]);

  const pv_to_load = pick(outputs?.PV, [
    "electric_to_load_series_kw",
    "pv_to_load_series_kw",
  ]);
  const pv_to_storage = pick(outputs?.PV, [
    "electric_to_storage_series_kw",
    "pv_to_storage_series_kw",
    "to_storage_series_kw",
  ]);
  const pv_to_grid = pick(outputs?.PV, [
    "electric_to_grid_series_kw",
    "electric_export_to_grid_series_kw",
    "to_grid_series_kw",
  ]);

  const es_to_load = pick(outputs?.ElectricStorage, [
    "electric_to_load_series_kw",
    "storage_to_load_series_kw",
    "discharge_to_load_series_kw",
    "discharge_series_kw",
  ]);
  const es_to_grid = pick(outputs?.ElectricStorage, [
    "electric_to_grid_series_kw",
    "storage_to_grid_series_kw",
    "discharge_to_grid_series_kw",
    "export_to_grid_series_kw",
  ]);
  const es_soc_pct = pick(outputs?.ElectricStorage, [
    "soc_series_pct",
    "state_of_charge_series_pct",
    "soc_pct_series",
    "soc_series_fraction",
  ]);

  // Check if this is an off-grid scenario
  const isOffGridScenario =
    results?.inputs?.Settings?.off_grid_flag ||
    results?.inputs?.Scenario?.off_grid_flag ||
    false;

  debug("[reoptToDailySeries] Off-grid detection:", isOffGridScenario);
  debug(
    "[reoptToDailySeries] results.inputs.Settings:",
    results?.inputs?.Settings,
  );
  debug(
    "[reoptToDailySeries] results keys:",
    results ? Object.keys(results) : "results is null",
  );
  debug("[reoptToDailySeries] Full results structure:", results);

  // For off-grid scenarios, don't extract utility data (should be null/empty anyway)
  const util_to_load = isOffGridScenario
    ? []
    : pick(outputs?.ElectricUtility, [
      "electric_to_load_series_kw",
      "grid_to_load_series_kw",
    ]);
  const util_to_storage = isOffGridScenario
    ? []
    : pick(outputs?.ElectricUtility, [
      "electric_to_storage_series_kw",
      "grid_to_storage_series_kw",
      "load_to_storage_series_kw",
      "from_grid_to_storage_series_kw",
    ]);

  const gen_to_load = pick(outputs?.Generator, [
    "electric_to_load_series_kw",
    "gen_to_load_series_kw",
  ]);
  debug("[reoptTransform] Generator data:", outputs?.Generator);
  debug(
    "[reoptTransform] gen_to_load extracted:",
    gen_to_load.slice(0, 24),
  ); // First 24 hours
  const gen_export = pick(outputs?.Generator, [
    "electric_to_grid_series_kw",
    "gen_to_grid_series_kw",
  ]);
  const gen_to_storage = pick(outputs?.Generator, [
    "electric_to_storage_series_kw",
    "gen_to_storage_series_kw",
    "to_storage_series_kw",
  ]);

  // Use provided loadYear, or extract from results, or fallback to current year
  const year = loadYear
    ? parseInt(loadYear, 10)
    : (results?.inputs?.Scenario?.analysis_year ||
      results?.inputs?.Financial?.analysis_year ||
      new Date().getFullYear());

  // Local midnight base
  const baseTimestamp = new Date(year, 0, 1).getTime();

  // Pre-calculate authoritative unmet load from REopt's official fields
  const critical_series = outputs?.ElectricLoad?.critical_load_series_kw || outputs?.ElectricLoad?.load_series_kw || [];
  const offgrid_met_series = outputs?.ElectricLoad?.offgrid_load_met_series_kw || [];

  // Debug critical vs regular load comparison
  if (isOffGridScenario) {
    const hasCriticalSeries = !!outputs?.ElectricLoad?.critical_load_series_kw;
    const hasRegularSeries = !!outputs?.ElectricLoad?.load_series_kw;
    debug("[reoptToDailySeries] Critical load series exists:", hasCriticalSeries);
    debug("[reoptToDailySeries] Regular load series exists:", hasRegularSeries);

    if (hasCriticalSeries && hasRegularSeries) {
      const criticalFirst24 = critical_series.slice(0, 24);
      const regularFirst24 = load.slice(0, 24);
      debug("[reoptToDailySeries] Critical load (first 24h):", criticalFirst24);
      debug("[reoptToDailySeries] Regular load (first 24h):", regularFirst24);

      const criticalSum = critical_series.reduce((a, b) => a + b, 0);
      const regularSum = load.reduce((a, b) => a + b, 0);
      debug("[reoptToDailySeries] Critical load total:", criticalSum);
      debug("[reoptToDailySeries] Regular load total:", regularSum);
      debug("[reoptToDailySeries] Critical/Regular ratio:", criticalSum / regularSum);
    }
  }

  // Authoritative unmet for off-grid: critical - firm load served
  const unmet_offgrid_kw = isOffGridScenario
    ? critical_series.map((v, i) => Math.max(0, (v ?? 0) - (offgrid_met_series[i] ?? 0)))
    : [];

  const stepsPerDay = 24 * tph;
  const start = dayIndex * stepsPerDay;
  const data = [];

  for (let i = 0; i < stepsPerDay; i++) {
    const idx = start + i;
    // Calculate timestamp: baseTimestamp is Jan 1 00:00, we add (dayIndex * 24 + hour) hours
    // Using 'i' for hour within day to get hour-beginning timestamps (00:00, 01:00, etc.)
    const dayTimestamp = baseTimestamp + (dayIndex * 24 * 3600 * 1000);
    const ts = dayTimestamp + (i * 3600 * 1000) / tph;
    const clamp = (v) => (Math.abs(v) < 1e-6 ? 0 : v);

    const load_kw = clamp(load[idx] ?? 0);
    const utility_to_load_kw = clamp(util_to_load[idx] ?? 0);
    const solar_to_load_kw = clamp(pv_to_load[idx] ?? 0);
    const bess_to_load_kw = clamp(es_to_load[idx] ?? 0);
    const diesel_to_load_kw = clamp(gen_to_load[idx] ?? 0);
    const solar_to_bess_kw = clamp(pv_to_storage[idx] ?? 0);
    const utility_to_bess_kw = clamp(util_to_storage[idx] ?? 0);
    const gen_to_bess_kw = clamp(gen_to_storage[idx] ?? 0);

    // In stacked area charts, negative values are used for charging flows so that they appear below the x-axis.
    // This visually distinguishes charging (negative, below axis) from discharging (positive, above axis) flows,
    // making it easier to interpret the direction of energy movement in the visualization.
    const bess_charge_solar_kw = -solar_to_bess_kw;
    const bess_charge_grid_kw = -utility_to_bess_kw;
    const bess_charge_gen_kw = -gen_to_bess_kw;

    // Use pre-calculated unmet from REopt's official fields for off-grid, else fall back to balance method for on-grid
    const utility_residual = isOffGridScenario && unmet_offgrid_kw[idx] !== undefined
      ? clamp(unmet_offgrid_kw[idx])
      : clamp(Math.max(0, load_kw - (utility_to_load_kw + bess_to_load_kw + solar_to_load_kw + diesel_to_load_kw)));
    data.push({
      timestamp: ts,
      hour: i / tph,
      load: load_kw,
      utility_residual,
      utility_to_load: utility_to_load_kw,
      bess_to_load: bess_to_load_kw,
      diesel_to_load: diesel_to_load_kw,
      solar_to_bess: solar_to_bess_kw,
      utility_to_bess: utility_to_bess_kw,
      gen_to_bess: gen_to_bess_kw,
      // PowerGraph keys
      utility: utility_to_load_kw,  // alias for grid_to_load in PowerGraph
      bess_discharge: bess_to_load_kw,
      genset: diesel_to_load_kw,
      bess_charge_solar: bess_charge_solar_kw,
      bess_charge_grid: bess_charge_grid_kw,
      bess_charge_gen: bess_charge_gen_kw,
      solar_to_load: solar_to_load_kw,
      solar_export: clamp(pv_to_grid[idx] ?? 0),
      bess_export: clamp(es_to_grid[idx] ?? 0),
      diesel_export: clamp(gen_export[idx] ?? 0),
      soc_pct: clamp(es_soc_pct[idx] ?? 0),
    });
  }

  // Add a 25th data point at 24:00 to complete the full day visualization
  if (data.length > 0) {
    const lastPoint = data[data.length - 1];
    const dayTimestamp = baseTimestamp + (dayIndex * 24 * 3600 * 1000);
    data.push({
      ...lastPoint,
      timestamp: dayTimestamp + (24 * 3600 * 1000), // 24:00 (midnight next day)
      hour: 24
    });
  }

  // Calculate infeasibility metrics for off-grid scenarios
  const infeasibilityMetrics = isOffGridScenario
    ? {
      hasUnmetLoad: data.some((point) => point.utility_residual > 0),
      totalUnmetLoad: data.reduce(
        (sum, point) => sum + Math.max(point.utility_residual, 0),
        0,
      ),
      offgridLoadMetFraction:
        results?.outputs?.offgrid_load_met_fraction || null,
    }
    : null;

  // Debug: Check if generator data made it into the final data
  if (isOffGridScenario) {
    const sampleHours = data.slice(0, 24);
    debug("[reoptTransform] First 24 hours of final data:");
    debug(
      "diesel_to_load:",
      sampleHours.map((h) => h.diesel_to_load),
    );
    debug(
      "genset:",
      sampleHours.map((h) => h.genset),
    );
    debug(
      "bess_to_load:",
      sampleHours.map((h) => h.bess_to_load),
    );
    debug(
      "bess_discharge:",
      sampleHours.map((h) => h.bess_discharge),
    );
    debug(
      "utility_residual:",
      sampleHours.map((h) => h.utility_residual || 0),
    );
  }

  return { data, isOffGrid: isOffGridScenario, infeasibilityMetrics };
}

/**
 * Transform raw REopt `results.outputs` into structured objects used by the UI.
 * The REopt API field names can vary between versions so this helper attempts to
 * pick the most common keys while being tolerant of missing data. All values
 * come directly from live REopt API responses.
 */
export function transformReoptOutputs(results) {
  const out = results?.outputs || {};
  const rawScenarios = out.scenarios || out.Scenarios;
  const scenarioEntries = rawScenarios
    ? Object.entries(rawScenarios)
    : [["default", out]];

  const scenarios = {};

  for (const [name, o] of scenarioEntries) {
    // Debug: log raw scenario object and financial mapping
    debug("[transformReoptOutputs] Scenario:", name, o);
    const fin = o.Financial || o;
    // First-year savings defined as sum of available year-1 savings components.
    const firstYearUtility = (o.utility_bill_savings_by_year || [])[0] || 0;
    const firstYearDemand = (o.demand_charge_savings_by_year || [])[0] || 0;
    const firstYearCapacity = (o.export_benefit_by_year || [])[0] || 0;
    const firstYearSavings =
      firstYearUtility + firstYearDemand + firstYearCapacity;

    const financial = {
      upfront_cost:
        fin.capital_costs_after_non_discounted_incentives ??
        fin.capital_costs_after_non_discounted_incentives_without_macrs ??
        fin.initial_capital_cost ??
        fin.total_installed_cost ??
        null,
      // Map net_savings to first-year savings per user request.
      net_savings: firstYearSavings || null,
      npv: fin.npv_us_dollars ?? fin.npv ?? null,
      payback: fin.payback_years ?? null,
      lcc: fin.lcc ?? fin.lifecycle_cost_us_dollars ?? null,
    };
    debug("[transformReoptOutputs] Financial:", financial);

    const performance = (o.utility_bill_savings_by_year || []).map(
      (v, idx) => ({
        year: idx + 1,
        utility_savings: v,
        demand_savings: o.demand_charge_savings_by_year?.[idx] ?? null,
        capacity_savings: o.export_benefit_by_year?.[idx] ?? null,
      }),
    );

    const resilience = {
      outage_duration_hours: o.resilience_hours ?? o.outage_duration ?? null,
      percent_load_served:
        o.probability_ensuring_power_to_critical_load ??
        o.percent_load_served ??
        null,
    };

    const emissions = {
      baseline_co2e_tons:
        o.emissions_cone_co2_baseline_tons ??
        o.co2_emissions_baseline_tons ??
        null,
      post_co2e_tons: o.emissions_cone_co2_tons ?? o.co2_emissions_tons ?? null,
    };

    const payments = (o.developer_annual_cashflow || []).map((v, idx) => ({
      year: idx + 1,
      amount: v,
    }));

    scenarios[name] = {
      financial,
      performance,
      resilience,
      emissions,
      payments,
    };
  }

  return { scenarios };
}
